import React, { startTransition } from "react";
import { DataPoint } from "../types";
import { useRef, useEffect, useMemo } from "react";
import {
  BufferGeometry,
  Float32BufferAttribute,
  PointsMaterial,
  Color,
} from "three";

export function PointCloud({
  points,
  maxZ = 20,
  minZ = -5,
}: {
  points: DataPoint[];
  maxZ?: number;
  minZ?: number;
}) {
  const geometryRef = useRef<BufferGeometry>(new BufferGeometry());

  useEffect(() => {
    if (!points || points.length === 0) return;
    startTransition(() => {
      const flat = new Float32Array(points.flat());

      // Create color array
      const colors = new Float32Array(points.length * 3);
      for (let i = 0; i < points.length; i++) {
        const z = flat[i * 3 + 2];
        const t = (z - minZ) / (maxZ - minZ); // Normalized height (0 to 1)

        // Create a gradient from blue (cold/low) to red (hot/high)
        // Hue: 0.6 (blue) to 0 (red)
        const color = new Color();
        color.setHSL(0.6 * (1 - t), 1, 0.5);

        colors[i * 3] = color.r;
        colors[i * 3 + 1] = color.g;
        colors[i * 3 + 2] = color.b;
      }

      if (
        geometryRef.current.getAttribute("position")?.count !== points.length
      ) {
        geometryRef.current.setAttribute(
          "position",
          new Float32BufferAttribute(flat, 3),
        );
        geometryRef.current.setAttribute(
          "color",
          new Float32BufferAttribute(colors, 3),
        );
      } else {
        // Update without reallocating new objects
        const posAttr = geometryRef.current.getAttribute("position");
        posAttr.array.set(flat);
        posAttr.needsUpdate = true;

        const colorAttr = geometryRef.current.getAttribute("color");
        colorAttr.array.set(colors);
        colorAttr.needsUpdate = true;
      }

      geometryRef.current.computeBoundingSphere();
    });
  }, [points]);

  const pointMaterial = useMemo(() => {
    const material = new PointsMaterial({
      size: 0.07,
      sizeAttenuation: true,
      vertexColors: true,
    });

    return material;
  }, []);

  if (!points || points.length === 0) return null;

  return (
    <points
      geometry={geometryRef.current}
      frustumCulled={false}
      material={pointMaterial}
    />
  );
}
